import { type ApiErrorCause, components, useApi } from "@appretio/api";
import { useQuery } from "@tanstack/react-query";

export type FeedbackRestore = components["schemas"]["FeedbackRestore"];

/**
 * Error carrying the HTTP status, so the restore page can tell "report not
 * found in this tenant" (404) apart from "not allowed to read reports" (403)
 * and everything else.
 */
export class GetFeedbackError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`get-feedback failed with status ${status}`);
    this.name = "GetFeedbackError";
    this.status = status;
  }
}

/**
 * Fetch the trimmed restore payload for one feedback report (v2 house
 * style: RPC POST). No retries — a 404/403 is an answer, not a flake — and
 * `meta.suppressErrorToast` so the app's global (never-dismissing) error
 * toast stays out of the way of the restore page's own 403 / 404 screens.
 *
 * Every failure surfaces as a {@link GetFeedbackError}: the api client's
 * auth middleware throws a plain Error carrying `cause.status` on non-2xx
 * responses, which is normalized here (status 0 = no HTTP answer at all).
 */
export function useGetFeedback(feedbackId: string | undefined) {
  const apiClient = useApi();
  return useQuery({
    queryKey: ["feedback", "get-feedback", feedbackId],
    enabled: Boolean(feedbackId),
    retry: false,
    meta: { suppressErrorToast: true },
    queryFn: async (): Promise<FeedbackRestore> => {
      try {
        const { data, response } = await apiClient.POST(
          "/v2/feedback/get-feedback",
          { body: { feedback_id: feedbackId as string } }
        );
        if (!data) {
          throw new GetFeedbackError(response.status);
        }
        return data.result;
      } catch (error) {
        if (error instanceof GetFeedbackError) throw error;
        const cause = (error as Error | undefined)?.cause as
          | ApiErrorCause
          | undefined;
        throw new GetFeedbackError(
          typeof cause?.status === "number" ? cause.status : 0
        );
      }
    }
  });
}
